const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

export const DATASET_TYPES = [
  { type: "aadhaar", label: "Aadhaar Status", description: "Student identity linkage and completion status.", iconClass: "" },
  { type: "apaar", label: "APAAR Entry", description: "APAAR ID capture, pending and mismatch records.", iconClass: "purple" },
  { type: "water", label: "Drinking Water", description: "Availability, functionality and infrastructure readiness.", iconClass: "green" },
  { type: "enrolment", label: "Enrolment", description: "Class-wise and school-wise student strength.", iconClass: "" },
  { type: "classroom", label: "Classroom / Toilet", description: "Physical facilities, usable rooms and sanitation assets.", iconClass: "orange" },
  { type: "data_entry", label: "Data Entry", description: "School-level form completion and missing records.", iconClass: "" },
  { type: "comparison", label: "Comparison", description: "Cross-period and cross-block comparative indicators.", iconClass: "purple" },
  { type: "remarks", label: "Remarks", description: "Comments, issue notes and follow-up observations.", iconClass: "green" },
  { type: "age", label: "Age-Wise", description: "Student distribution by age band and grade.", iconClass: "" },
  { type: "teacher", label: "Teacher Data", description: "Teacher profile, deployment and certification records.", iconClass: "orange" },
];

const TEMPLATE_DEFINITIONS = [
  {
    key: "school_master",
    title: "School Master Columns",
    description: "District, block, UDISE code, school name and management type.",
    datasets: ["enrolment", "data_entry", "comparison"],
  },
  {
    key: "student_identity",
    title: "Student Identity Columns",
    description: "Aadhaar status, APAAR status, class, gender and age bands.",
    datasets: ["aadhaar", "apaar", "age", "enrolment"],
  },
  {
    key: "infrastructure",
    title: "Infrastructure Columns",
    description: "Water, classroom, toilet and facility indicators.",
    datasets: ["water", "classroom"],
  },
  {
    key: "remarks",
    title: "Remarks and Exceptions",
    description: "Free-text comments are stored separately for audit review.",
    datasets: ["remarks"],
  },
  {
    key: "teacher",
    title: "Teacher Data Columns",
    description: "Profile, deployment, training and certification details.",
    datasets: ["teacher"],
  },
];

export const buildTemplateRows = (imports = [], stats = null) => {
  const completedTypes = new Set(
    imports.filter((item) => item.status === "completed").map((item) => item.dataset_type).filter(Boolean)
  );

  return TEMPLATE_DEFINITIONS.map((row) => {
    const imported = row.datasets.some((type) => completedTypes.has(type));
    const schoolsLoaded = (stats?.total_schools_in_db ?? 0) > 0;
    let tag = "Required";
    let tagClass = "green";
    if (imported) {
      tag = "Imported";
      tagClass = "green";
    } else if (row.key === "remarks") {
      tag = "Optional";
      tagClass = "orange";
    } else if (schoolsLoaded) {
      tag = "Mapped";
      tagClass = "";
    }
    return {
      title: row.title,
      description: row.description,
      tag,
      tagClass,
    };
  });
};

export const buildDatasetCatalog = (imports = []) => {
  const counts = {};
  imports.forEach((item) => {
    if (!item.dataset_type) return;
    counts[item.dataset_type] = (counts[item.dataset_type] || 0) + 1;
  });

  return DATASET_TYPES.map((item) => ({
    ...item,
    importCount: counts[item.type] || 0,
    description:
      counts[item.type] > 0
        ? `${item.description} ${counts[item.type]} completed import${counts[item.type] === 1 ? "" : "s"} on record.`
        : item.description,
  }));
};

const DATASET_LABELS = {
  aadhaar: "Aadhaar Status",
  apaar: "APAAR Entry",
  water: "Drinking Water",
  enrolment: "Enrolment",
  teacher: "Teacher Data",
  classroom: "Classroom / Toilet",
  data_entry: "Data Entry",
  comparison: "Comparison",
  remarks: "Remarks",
  age: "Age-wise",
};

export const getDatasetLabel = (type) => DATASET_LABELS[type] || type || "Unknown";

export const buildImportKpis = (stats = null) => {
  const schools = stats?.total_schools_in_db ?? 0;
  const completed = stats?.completed_imports ?? 0;
  const districts = stats?.unique_districts ?? 0;
  const source = stats?.data_source;
  const loaded = source === "imported";

  return [
    {
      label: "Schools in Database",
      value: fmt(schools),
      note: schools > 0 ? "School master records available" : "Awaiting first school master upload",
      iconClass: "",
    },
    {
      label: "Completed Imports",
      value: fmt(completed),
      note: completed > 0 ? "Validated imports published" : "No file has passed validation yet",
      iconClass: "green",
    },
    {
      label: "Districts Covered",
      value: fmt(districts),
      note: districts > 0 ? "District coverage active" : "Coverage will update after import",
      iconClass: "purple",
    },
    {
      label: "Data Source",
      value: loaded ? "Imported" : "—",
      note: loaded ? "Production dataset loaded" : "Upload Excel or import from URL",
      statusPill: loaded ? "Loaded" : "Not Loaded",
      statusClass: loaded ? "success" : "",
    },
  ];
};

export const buildReadinessState = (imports = [], uploading = false, importingUrl = false) => {
  const active = imports.find((i) => i.status === "processing" || i.status === "pending");
  const latest = imports[0];
  const hasCompleted = imports.some((i) => i.status === "completed");
  const hasFailed = imports.some((i) => i.status === "failed");

  let pct = 0;
  let headline = "No source loaded";
  let narrative = "Upload a file to begin automated validation. Once validation is complete, the publish button becomes available.";
  let statusPill = "Pending";
  let statusClass = "";
  let ringColor = "#f59e0b";

  if (uploading || importingUrl) {
    pct = 25;
    headline = "Upload in progress";
    narrative = "File received. Automated validation will begin once upload completes.";
    statusPill = "Uploading";
  } else if (active) {
    pct = active.status === "processing" ? 55 : 35;
    headline = "Validation running";
    narrative = "Columns, formats and duplicate checks are being processed before publish.";
    statusPill = "Processing";
  } else if (hasCompleted) {
    pct = 100;
    headline = "Ready to publish";
    narrative = "Latest import passed validation. Dashboards can refresh from the published dataset.";
    statusPill = "Ready";
    statusClass = "success";
    ringColor = "#10b981";
  } else if (hasFailed) {
    pct = 40;
    headline = "Validation issues found";
    narrative = "Review failed import details, fix the source file and upload again.";
    statusPill = "Needs review";
  } else if (latest) {
    pct = 20;
    headline = "Source received";
    narrative = "Import queued. Validation will update readiness automatically.";
  }

  const steps = [
    {
      title: "Upload Source",
      description: "Excel file or verified public URL.",
      status: uploading || importingUrl ? "In progress" : latest || active ? "Done" : "Waiting",
      active: !(latest || active) && !uploading && !importingUrl,
    },
    {
      title: "Validate Structure",
      description: "Columns, formats, mandatory fields and duplicates.",
      status: active?.status === "processing" ? "In progress" : hasCompleted ? "Done" : latest || active ? "Waiting" : "Locked",
      active: Boolean(active),
    },
    {
      title: "Review Preview",
      description: "Approve sample rows and exceptions before publish.",
      status: hasCompleted ? "Done" : active ? "Waiting" : "Locked",
      active: false,
    },
    {
      title: "Publish Dataset",
      description: "Dashboards refresh after successful import.",
      status: hasCompleted ? "Done" : "Locked",
      active: false,
    },
  ];

  return {
    pct,
    headline,
    narrative,
    statusPill,
    statusClass,
    ringStyle: {
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${ringColor} 0 ${pct}%, rgba(245,158,11,.14) ${pct}% 100%)`,
      "--di-ring-color": ringColor,
    },
    steps,
  };
};

export const filterImports = (imports = [], filter = "all") => {
  if (filter === "successful") return imports.filter((i) => i.status === "completed");
  if (filter === "warnings") return imports.filter((i) => i.status === "pending" || i.status === "processing");
  if (filter === "failed") return imports.filter((i) => i.status === "failed");
  return imports;
};

export const mapImportRow = (imp) => ({
  id: imp.import_id,
  dataset: getDatasetLabel(imp.dataset_type),
  source: imp.filename || imp.source_url || "Upload",
  rows: fmt(imp.records_processed ?? 0),
  validation: imp.validation_summary || (imp.status === "completed" ? "Passed" : imp.status === "failed" ? "Failed" : "Pending"),
  importedBy: imp.imported_by || imp.user || "System",
  time: imp.created_at ? new Date(imp.created_at).toLocaleString("en-IN") : "—",
  status: imp.status || "pending",
});

export const getStatusTagClass = (status) => {
  if (status === "completed") return "green";
  if (status === "failed") return "red";
  if (status === "processing" || status === "pending") return "orange";
  return "";
};

export const buildDataImportMarkdown = (imports = [], uploading = false, importingUrl = false) => {
  const state = buildReadinessState(imports, uploading, importingUrl);
  const kpis = buildImportKpis(imports, null);
  return [
    "## Insights",
    `- ${state.headline}: ${state.narrative}`,
    ...kpis.slice(0, 3).map((k) => `- ${k.label}: ${k.value} (${k.note})`),
    "",
    "## Priority Action Items",
    ...state.steps
      .filter((s) => s.status !== "Done")
      .slice(0, 3)
      .map((s) => `- ${s.title}: ${s.description}`),
  ].join("\n");
};
